/**
 * AuthContext - AWS Cognito Authentication
 *
 * Provides authentication state and methods using AWS Amplify Auth (Cognito).
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  getCurrentUser,
  fetchAuthSession,
  signOut as amplifySignOut,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { api } from '../../lib/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch and set the current authenticated user
   */
  const loadUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();

      // Combine user info with attributes
      const userData = {
        id: currentUser.userId,
        username: currentUser.username,
        email: attributes.email,
        ...attributes,
      };

      setUser(userData);

      // Fetch user profile with clinic info from our API
      try {
        const profileData = await api.getCurrentProfile();
        setProfile(profileData);
      } catch (profileError) {
        console.warn('Could not fetch profile:', profileError);
        // User is authenticated but may not have a profile yet
        setProfile(null);
      }
    } catch (error) {
      // Not authenticated
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Sign out the current user
   */
  const logout = async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setProfile(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Force clear state even if signOut fails
      setUser(null);
      setProfile(null);
    }
  };

  /**
   * Get the current session (for accessing tokens)
   */
  const getSession = async () => {
    try {
      const session = await fetchAuthSession();
      return session;
    } catch (error) {
      console.error('Failed to get session:', error);
      return null;
    }
  };

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = () => {
    return !!user;
  };

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Listen for auth events from Amplify Hub
  useEffect(() => {
    const hubListener = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          setLoading(true);
          loadUser();
          break;
        case 'signedOut':
          setUser(null);
          setProfile(null);
          break;
        case 'tokenRefresh':
          // Token was refreshed, no action needed
          break;
        case 'tokenRefresh_failure':
          // Token refresh failed, user may need to re-authenticate
          console.warn('Token refresh failed');
          break;
        default:
          break;
      }
    });

    // Cleanup listener on unmount
    return () => hubListener();
  }, [loadUser]);

  const value = {
    user,
    profile,
    loading,
    logout,
    getSession,
    isAuthenticated,
    refreshUser: loadUser,
    // Convenience accessors for common profile fields
    clinicId: profile?.clinic_id || null,
    role: profile?.role || null,
    isSuperAdmin: profile?.role === 'super_admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
